import React from 'react';
import { Button, Stack, Theme, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseRounded';
import { SortableList } from '../../dnd/SortableList';
import { WaiverOption } from '../../../types/waivers';
import { OPTIONS_STYLES } from '../../../constants/waivers';

interface Props extends React.PropsWithChildren {
  options: WaiverOption[];
  onChange: (newOptions: WaiverOption[]) => void;
  item: WaiverOption;
  disabled?: boolean;
  isLargeItem?: boolean;
}

function OptionActions({
  options,
  onChange,
  item,
  offset = 0,
  alwaysVisible,
  disabled,
}: Omit<Props, 'placeholder'> & {
  item: WaiverOption;
  offset?: number;
  alwaysVisible?: boolean;
}) {
  return (
    <Stack
      ml="auto"
      position={alwaysVisible ? 'relative' : 'absolute'}
      display={alwaysVisible ? 'flex' : 'none'}
      top={alwaysVisible ? undefined : 0}
      bottom={alwaysVisible ? undefined : 0}
      left={alwaysVisible ? undefined : `calc(100% + ${offset}px)`}
      boxSizing="border-box"
      width={{ xs: `${OPTIONS_STYLES.rightItemWidth}px`, lg: 'auto' }}
      className="item-actions"
      flexDirection={{ xs: 'column', lg: 'row' }}
      alignItems="center"
      gap={0.5}>
      <SortableList.DragHandle />
      <Button
        disabled={disabled}
        type="button"
        variant="link"
        onClick={() => {
          const newOptions = options.filter(option => option.id !== item.id);
          onChange(newOptions);
        }}
        sx={theme => ({
          color: theme.palette.outlineGrey.main,
          minWidth: 0,
          borderRadius: '50%',
          aspectRatio: 1,
          padding: 0,
          ':hover': {
            backgroundColor: theme.palette.lightOutlineGrey.main,
            color: '#fff',
          },
        })}>
        <CloseIcon fontSize="small" />
      </Button>
    </Stack>
  );
}

function OptionsListItem({
  item,
  disabled,
  children,
  options,
  onChange,
  isLargeItem,
}: Props) {
  const isLarge = useMediaQuery<Theme>(theme => theme.breakpoints.up('lg'));

  const actionsAlwaysVisible = !isLarge;

  return (
    <SortableList.Item
      id={item.id}
      disabled={disabled}
      style={{
        width: isLargeItem ? '100%' : undefined,
        padding: 0,
      }}>
      <Stack
        width="100%"
        boxSizing="border-box"
        sx={{
          ':hover': {
            '& .item-actions': {
              display: disabled ? undefined : 'flex',
            },
          },
        }}>
        <Stack
          position="relative"
          boxSizing="border-box"
          direction="row"
          alignItems={isLargeItem ? 'center' : 'flex-start'}
          justifyContent="flex-start"
          width={{ xs: '100%', lg: isLargeItem ? '100%' : 'fit-content' }}
          maxWidth="100%">
          <Stack
            gap={`${OPTIONS_STYLES.gap}px`}
            direction="row"
            width={isLargeItem ? '100%' : undefined}
            maxWidth={`calc(100% - ${
              actionsAlwaysVisible ? OPTIONS_STYLES.rightItemWidth : 0
            }px)`}
            alignItems="flex-start"
            justifyContent="flex-start">
            {children}
          </Stack>
          <OptionActions
            alwaysVisible={actionsAlwaysVisible}
            options={options}
            onChange={onChange}
            item={item}
            disabled={disabled}
          />
        </Stack>
      </Stack>
    </SortableList.Item>
  );
}

export default OptionsListItem;
